import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Card,
  Form,
  Radio,
  Typography,
  Divider,
  Alert,
  Space,
  Avatar,
  Row,
  Col,
  Switch,
  Collapse,
  Button,
  message,
  Input,
  InputNumber,
  Select,
  Progress
} from 'antd';
import {
  TeamOutlined,
  ApartmentOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import CMTStructureVisualization from '../shared/CMTStructureVisualization';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * 📝 Generic CMT List Input Component - Supports Regional, Country, and Site CMTs
 * Moved outside main component to prevent re-creation and focus loss
 */
const CMTListInput = memo(({ value = [], onChange, fields, cmtType = 'CMT' }) => {
  // Generate stable unique keys for each item
  const itemsWithKeys = useMemo(() => {
    return value.map((item, index) => ({
      ...item,
      _uniqueKey: item._uniqueKey || `${cmtType.toLowerCase()}_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`
    }));
  }, [value, cmtType]);

  const addItem = useCallback(() => {
    const newItem = {
      _uniqueKey: `${cmtType.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    fields.forEach(field => {
      newItem[field.key] = '';
    });
    onChange([...value, newItem]);
  }, [value, onChange, fields, cmtType]);

  const removeItem = useCallback((index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  }, [value, onChange]);

  const updateItem = useCallback((index, key, val) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [key]: val };
    onChange(newValue);
  }, [value, onChange]);

  return (
    <div>
      {itemsWithKeys.map((item, index) => (
        <div key={item._uniqueKey} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>{cmtType} CMT #{index + 1}</Text>
            <Button 
              type="text" 
              danger 
              icon={<MinusCircleOutlined />} 
              onClick={() => removeItem(index)}
              size="small"
            >
              Remove
            </Button>
          </div>
          
          {fields.map(field => (
            <div key={field.key} style={{ marginBottom: '8px' }}>
              <Text style={{ display: 'block', marginBottom: '4px' }}>{field.label}:</Text>
              <Input
                placeholder={field.placeholder}
                value={item[field.key] || ''}
                onChange={(e) => updateItem(index, field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      ))}

      <Button 
        type="dashed" 
        onClick={addItem} 
        block 
        icon={<PlusOutlined />}
        style={{ marginTop: '8px' }}
      >
        Add {cmtType} CMT
      </Button>
    </div>
  );
});

// Legacy alias for backward compatibility
const RegionalCMTListInput = (props) => <CMTListInput {...props} cmtType="Regional" />;

/**
 * 🏗️ Multi-CMT Configuration Component
 * 
 * Onboarding step for configuring multi-CMT organizational structure.
 * Offers simple single-CMT setup or advanced multi-CMT templates.
 * 
 * Features:
 * - Single vs Multi-CMT selection
 * - Organizational structure templates
 * - Advanced configuration options
 * - Backward compatibility with existing single-CMT setups
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - Option B Administrative Tools - Onboarding Enhancement
 */

const MultiCMTConfiguration = ({ 
  form, 
  onHierarchyChange = null, 
  initialData = null, 
  isAdminMode = false 
}) => {
  const [selectedStructure, setSelectedStructure] = useState('single');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [configurationStep, setConfigurationStep] = useState('selection'); // 'selection' | 'qa' | 'preview'
  const [cmtConfiguration, setCmtConfiguration] = useState({});
  const [regionalCount, setRegionalCount] = useState(2);
  const [regionCount, setRegionCount] = useState(3);
  
  // Q&A Flow State Management
  const [qaStep, setQaStep] = useState(0);
  const [qaAnswers, setQaAnswers] = useState({});
  const [hierarchyData, setHierarchyData] = useState(null);

  // Initialize with existing data in admin mode
  useEffect(() => {
    if (isAdminMode && initialData) {
      console.log('🔄 Initializing MultiCMTConfiguration with existing data:', initialData);
      setHierarchyData(initialData);
      setSelectedStructure('multi');
      setSelectedTemplate('enterprise-global'); // Default for editing
      setConfigurationStep('qa');
      
      // Initialize Q&A answers from existing hierarchy
      if (initialData.root) {
        const initAnswers = {
          globalName: initialData.root.name || 'Global Crisis Management Team'
        };
        
        // Extract regional data
        if (initialData.root.children && initialData.root.children.length > 0) {
          initAnswers.needsRegional = 'true';
          initAnswers.regionalCMTs = initialData.root.children.map(child => ({
            name: child.name,
            scope: child.scope || ''
          }));
        } else {
          initAnswers.needsRegional = 'false';
        }
        
        setQaAnswers(initAnswers);
      }
    }
  }, [isAdminMode, initialData]);

  // Organizational structure options
  const STRUCTURE_OPTIONS = [
    {
      id: 'single',
      name: 'Single Crisis Management Team',
      description: 'Traditional single-team structure for most organizations',
      icon: <TeamOutlined style={{ color: '#1890ff' }} />,
      recommended: true,
      details: [
        'One centralized crisis management team',
        'Simple command structure',
        'Best for small to medium organizations',
        'Easy to manage and coordinate',
        'Recommended for most clients'
      ]
    },
    {
      id: 'multi',
      name: 'Multi-CMT Enterprise Structure',
      description: 'Advanced hierarchical structure for large organizations',
      icon: <ApartmentOutlined style={{ color: '#722ed1' }} />,
      recommended: false,
      details: [
        'Multiple crisis management teams',
        'Hierarchical command structure',
        'Best for large enterprises',
        'Regional or functional specialization',
        'Advanced coordination capabilities'
      ]
    }
  ];

  // Multi-CMT templates (only shown if multi-CMT is selected)
  const MULTI_CMT_TEMPLATES = [
    {
      id: 'enterprise-global',
      name: 'Enterprise Global Structure',
      description: 'Global oversight with regional divisions',
      icon: <GlobalOutlined style={{ color: '#1890ff' }} />,
      structure: 'Global HQ → Regional Teams',
      example: 'Global Crisis Team → North America, EMEA, APAC'
    },
    {
      id: 'functional-matrix',
      name: 'Functional Matrix Structure', 
      description: 'Specialized teams by function',
      icon: <ToolOutlined style={{ color: '#722ed1' }} />,
      structure: 'Executive → Functional Teams',
      example: 'Executive Team → IT Security, Communications, Legal'
    },
    {
      id: 'regional-hub',
      name: 'Regional Hub Structure',
      description: 'Geographic regions with local sites',
      icon: <EnvironmentOutlined style={{ color: '#52c41a' }} />,
      structure: 'National → Regional → Local',
      example: 'National Center → Regional Hubs → Site Teams'
    }
  ];

  /**
   * 🔄 Handle structure selection change
   */
  const handleStructureChange = (value) => {
    setSelectedStructure(value);
    setSelectedTemplate(null);
    
    // Update form values
    form.setFieldsValue({
      cmtStructureType: value,
      cmtTemplate: null,
      advancedCmtConfig: false
    });
  };

  /**
   * 🎯 Handle template selection - now leads to Q&A flow
   */
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setConfigurationStep('qa');
    setQaStep(0);
    setQaAnswers({});
    form.setFieldsValue({
      cmtTemplate: templateId
    });
  };

  /**
   * 📋 Dynamic Q&A Questions Generation - Complete 4-Tier System
   */
  const getQAQuestions = (templateId, currentAnswers = {}) => {
    if (templateId === 'enterprise-global') {
      const questions = [
        {
          id: 'globalName',
          type: 'text',
          question: 'What is your top-level Crisis Management Team called?',
          placeholder: 'e.g., Global Crisis Command',
          required: true,
          context: 'This is your highest-level CMT that provides overall crisis oversight and coordination.'
        },
        {
          id: 'needsRegional',
          type: 'radio',
          question: 'Does your organization need Regional-level CMTs?',
          context: 'Regional CMTs manage crisis response across multiple countries or large geographic areas.',
          options: [
            { value: 'true', label: 'Yes - We need regional crisis management teams' },
            { value: 'false', label: 'No - Our top-level CMT manages everything directly' }
          ],
          required: true
        }
      ];

      // Add regional CMT collection if needed
      if (currentAnswers.needsRegional === 'true') {
        questions.push({
          id: 'regionalCMTs',
          type: 'list',
          question: 'What Regional-level CMTs do you need?',
          context: 'Add each regional CMT with its name and geographic scope.',
          fields: [
            { key: 'name', label: 'Regional CMT Name', placeholder: 'e.g., APAC Regional CMT' },
            { key: 'scope', label: 'Geographic Scope', placeholder: 'e.g., Asia Pacific operations and coordination' }
          ],
          required: true
        });
      }

      // Dynamic country-level questions for each regional CMT
      if (currentAnswers.regionalCMTs && currentAnswers.regionalCMTs.length > 0) {
        currentAnswers.regionalCMTs.forEach((regional, index) => {
          // Ask if this regional needs country CMTs
          questions.push({
            id: `regional_${index}_needsCountries`,
            type: 'radio',
            question: `Does ${regional.name} need country-level CMTs?`,
            context: 'Country CMTs handle local regulations, language requirements, and national emergency coordination.',
            options: [
              { value: 'true', label: 'Yes - This region needs separate CMTs for different countries' },
              { value: 'false', label: 'No - The regional CMT manages all countries directly' }
            ],
            required: true,
            regionalIndex: index
          });

          // Collect country CMTs if needed
          if (currentAnswers[`regional_${index}_needsCountries`] === 'true') {
            questions.push({
              id: `regional_${index}_countryCMTs`,
              type: 'list',
              question: `What country-level CMTs does ${regional.name} need?`,
              context: 'Add each country CMT with its name and national scope.',
              fields: [
                { key: 'name', label: 'Country CMT Name', placeholder: 'e.g., Australia Crisis Management Team' },
                { key: 'scope', label: 'National Scope', placeholder: 'e.g., Australian operations and regulatory compliance' }
              ],
              required: true,
              regionalIndex: index
            });
          }
        });
      }

      // Dynamic site-level questions for each country CMT
      if (currentAnswers.regionalCMTs && currentAnswers.regionalCMTs.length > 0) {
        currentAnswers.regionalCMTs.forEach((regional, regIndex) => {
          const countryCMTs = currentAnswers[`regional_${regIndex}_countryCMTs`];
          if (countryCMTs && countryCMTs.length > 0) {
            countryCMTs.forEach((country, countryIndex) => {
              // Ask if this country needs site CMTs
              questions.push({
                id: `country_${regIndex}_${countryIndex}_needsSites`,
                type: 'radio',
                question: `Does ${country.name} need site-level CMTs?`,
                context: 'Site CMTs handle facility-specific emergency response, local evacuations, and on-ground coordination.',
                options: [
                  { value: 'true', label: 'Yes - This country needs separate CMTs for different sites/facilities' },
                  { value: 'false', label: 'No - The country CMT manages all sites directly' }
                ],
                required: true,
                regionalIndex: regIndex,
                countryIndex: countryIndex
              });

              // Collect site CMTs if needed
              if (currentAnswers[`country_${regIndex}_${countryIndex}_needsSites`] === 'true') {
                questions.push({
                  id: `country_${regIndex}_${countryIndex}_siteCMTs`,
                  type: 'list',
                  question: `What site-level CMTs does ${country.name} need?`,
                  context: 'Add each site CMT with its name and facility scope.',
                  fields: [
                    { key: 'name', label: 'Site CMT Name', placeholder: 'e.g., Sydney Operations Crisis Team' },
                    { key: 'scope', label: 'Facility Scope', placeholder: 'e.g., Sydney office building and surrounding facilities' }
                  ],
                  required: true,
                  regionalIndex: regIndex,
                  countryIndex: countryIndex
                });
              }
            });
          }
        });
      }

      return questions;
    }
    
    // Add other template Q&A flows here
    return [];
  };

  /**
   * 🔄 Handle Q&A progression - Dynamic Question System
   */
  const handleQANext = (answer) => {
    const newAnswers = { ...qaAnswers, [getCurrentQuestion().id]: answer };
    setQaAnswers(newAnswers);

    // Get current dynamic questions based on all answers so far
    const questions = getQAQuestions(selectedTemplate, newAnswers);
    const nextStep = qaStep + 1;

    if (nextStep >= questions.length) {
      // Q&A complete, generate hierarchy preview
      generateHierarchyPreview(newAnswers);
      setConfigurationStep('preview');
    } else {
      setQaStep(nextStep);
    }
  };

  const handleQABack = () => {
    if (qaStep > 0) {
      setQaStep(qaStep - 1);
    } else {
      setConfigurationStep('selection');
    }
  };

  const getCurrentQuestion = () => {
    const questions = getQAQuestions(selectedTemplate, qaAnswers);
    return questions[qaStep];
  };

  /**
   * 🎨 Generate complete 4-tier hierarchy preview from Q&A answers
   */
  const generateHierarchyPreview = (answers) => {
    if (selectedTemplate === 'enterprise-global') {
      const hierarchy = {
        root: {
          id: 'global_cmt',
          name: answers.globalName || 'Global Crisis Management Team',
          type: 'Global',
          scope: 'Worldwide oversight and coordination',
          children: []
        }
      };

      // Add regional CMTs
      if (answers.needsRegional === 'true' && answers.regionalCMTs) {
        answers.regionalCMTs.forEach((regional, regIndex) => {
          const regionalNode = {
            id: `regional_${regIndex}`,
            name: regional.name,
            type: 'Regional',
            scope: regional.scope,
            children: []
          };

          // Add country CMTs for this regional
          const needsCountries = answers[`regional_${regIndex}_needsCountries`];
          const countryCMTs = answers[`regional_${regIndex}_countryCMTs`];
          
          if (needsCountries === 'true' && countryCMTs) {
            countryCMTs.forEach((country, countryIndex) => {
              const countryNode = {
                id: `country_${regIndex}_${countryIndex}`,
                name: country.name,
                type: 'Country',
                scope: country.scope,
                children: []
              };

              // Add site CMTs for this country
              const needsSites = answers[`country_${regIndex}_${countryIndex}_needsSites`];
              const siteCMTs = answers[`country_${regIndex}_${countryIndex}_siteCMTs`];
              
              if (needsSites === 'true' && siteCMTs) {
                siteCMTs.forEach((site, siteIndex) => {
                  countryNode.children.push({
                    id: `site_${regIndex}_${countryIndex}_${siteIndex}`,
                    name: site.name,
                    type: 'Site',
                    scope: site.scope,
                    children: []
                  });
                });
              }

              regionalNode.children.push(countryNode);
            });
          }

          hierarchy.root.children.push(regionalNode);
        });
      }

      setHierarchyData(hierarchy);

      // Notify parent component of hierarchy changes (for admin mode)
      if (isAdminMode && onHierarchyChange) {
        onHierarchyChange(hierarchy);
      }

      // Store complete configuration in form for use in next step
      form.setFieldsValue({
        hierarchyData: hierarchy,  // THIS IS THE KEY FIELD FOR CMT ASSIGNMENT!
        cmtConfiguration: {
          globalName: answers.globalName,
          hasRegional: answers.needsRegional,
          regional: answers.regionalCMTs || [],
          fullHierarchy: hierarchy,
          qaAnswers: answers
        }
      });
    }
  };

  /**
   * 📝 Render Q&A Interface
   */
  const renderQAInterface = () => {
    const question = getCurrentQuestion();
    if (!question) return null;

    // Check if question should be shown based on conditional logic
    if (question.conditional && !question.conditional(qaAnswers)) {
      // Skip this question, move to next
      handleQANext(null);
      return null;
    }

    const allQuestions = getQAQuestions(selectedTemplate, qaAnswers);
    const totalQuestions = allQuestions.length;

    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <Text type="secondary">Question {qaStep + 1}{totalQuestions > 0 ? ` of ${totalQuestions}` : ''}</Text>
          {totalQuestions > 3 && (
            <div style={{ marginTop: '8px' }}>
              <Progress percent={Math.round(((qaStep + 1) / totalQuestions) * 100)} size="small" />
            </div>
          )}
        </div>

        <Card>
          <Title level={4} style={{ marginBottom: '16px' }}>
            {question.question}
          </Title>
          
          {question.context && (
            <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
              {question.context}
            </Paragraph>
          )}

          {renderQuestionInput(question)}

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleQABack}>
              <ArrowLeftOutlined /> Back
            </Button>
            
            <Button 
              type="primary" 
              onClick={() => {
                const answer = getQuestionAnswer(question);
                if (validateAnswer(question, answer)) {
                  handleQANext(answer);
                }
              }}
            >
              {qaStep === totalQuestions - 1 ? 'Generate Preview' : 'Next'}
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  /**
   * 🎛️ Render input based on question type - Optimized with stable references
   */
  const updateQuestionAnswer = useCallback((questionId, value) => {
    setQaAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const renderQuestionInput = (question) => {
    const currentValue = qaAnswers[question.id] || (question.type === 'list' ? [] : '');

    const handleTextChange = (e) => {
      updateQuestionAnswer(question.id, e.target.value);
    };

    const handleRadioChange = (e) => {
      updateQuestionAnswer(question.id, e.target.value);
    };

    const handleListChange = (value) => {
      updateQuestionAnswer(question.id, value);
    };

    switch (question.type) {
      case 'text':
        return (
          <Input
            placeholder={question.placeholder}
            value={currentValue}
            onChange={handleTextChange}
            size="large"
          />
        );

      case 'radio':
        return (
          <Radio.Group
            value={currentValue}
            onChange={handleRadioChange}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {question.options.map(option => (
                <Radio key={option.value} value={option.value} style={{ padding: '8px 0' }}>
                  {option.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'list':
        // Determine CMT type from question ID - precise pattern matching
        let cmtType = 'Regional';
        if (question.id.includes('siteCMTs')) {
          cmtType = 'Site';
        } else if (question.id.includes('countryCMTs')) {
          cmtType = 'Country';
        }
        return <CMTListInput value={currentValue} onChange={handleListChange} fields={question.fields} cmtType={cmtType} />;

      default:
        return null;
    }
  };



  /**
   * ✅ Get current answer for validation
   */
  const getQuestionAnswer = (question) => {
    return qaAnswers[question.id] || (question.type === 'list' ? [] : '');
  };

  /**
   * 🔍 Validate question answer
   */
  const validateAnswer = (question, answer) => {
    if (question.required) {
      if (question.type === 'list') {
        if (!answer || answer.length === 0) {
          message.error('Please add at least one item');
          return false;
        }
      } else if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
        message.error('This field is required');
        return false;
      }
    }
    return true;
  };

  /**
   * 🔄 Handle going back to template selection
   */
  const handleBackToSelection = () => {
    setConfigurationStep('selection');
    setCmtConfiguration({});
  };

  /**
   * ✅ Handle configuration completion
   */
  const handleConfigurationComplete = (config) => {
    setCmtConfiguration(config);
    form.setFieldsValue({
      cmtConfiguration: config
    });
    message.success('CMT structure configured successfully!');
  };

  /**
   * 🎨 Render structure option card
   */
  const renderStructureOption = (option) => (
    <Card
      key={option.id}
      hoverable
      className="structure-option-card"
      style={{ 
        marginBottom: '16px',
        border: selectedStructure === option.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
        backgroundColor: selectedStructure === option.id ? '#f0f8ff' : 'transparent'
      }}
      bodyStyle={{ padding: '16px' }}
      onClick={() => handleStructureChange(option.id)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Avatar 
          size="large" 
          icon={option.icon} 
          style={{ marginRight: '16px', flexShrink: 0 }}
        />
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <Title level={5} style={{ margin: 0, marginRight: '8px' }}>
              {option.name}
            </Title>
            {option.recommended && (
              <span style={{ 
                backgroundColor: '#52c41a', 
                color: 'white', 
                fontSize: '10px', 
                padding: '2px 6px', 
                borderRadius: '4px',
                fontWeight: 'bold'
              }}>
                RECOMMENDED
              </span>
            )}
          </div>
          
          <Paragraph style={{ color: '#666', marginBottom: '12px', fontSize: '14px' }}>
            {option.description}
          </Paragraph>
          
          <div style={{ fontSize: '12px', color: '#888' }}>
            {option.details.map((detail, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                • {detail}
              </div>
            ))}
          </div>
        </div>
        
        <Radio 
          checked={selectedStructure === option.id}
          style={{ marginLeft: '8px' }}
        />
      </div>
    </Card>
  );

  /**
   * 🎯 Render multi-CMT template cards
   */
  const renderTemplateCards = () => {
    if (selectedStructure !== 'multi') return null;

    return (
      <div style={{ marginTop: '24px' }}>
        <Divider />
        
        <Title level={4} style={{ marginBottom: '16px' }}>
          <SettingOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
          Choose Multi-CMT Template
        </Title>
        
        <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
          Select an organizational template that best matches your enterprise structure. 
          You can customize it further after onboarding.
        </Paragraph>

        <Row gutter={[16, 16]}>
          {MULTI_CMT_TEMPLATES.map(template => (
            <Col span={24} key={template.id}>
              <Card
                hoverable
                style={{ 
                  border: selectedTemplate === template.id ? '2px solid #722ed1' : '1px solid #d9d9d9',
                  backgroundColor: selectedTemplate === template.id ? '#faf5ff' : 'transparent'
                }}
                bodyStyle={{ padding: '16px' }}
                onClick={() => handleTemplateSelect(template.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    size="default" 
                    icon={template.icon} 
                    style={{ marginRight: '12px' }}
                  />
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <Text strong style={{ fontSize: '14px' }}>{template.name}</Text>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
                          {template.description}
                        </div>
                      </div>
                      
                      <Radio checked={selectedTemplate === template.id} />
                    </div>
                    
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#888' }}>
                      <div><strong>Structure:</strong> {template.structure}</div>
                      <div><strong>Example:</strong> {template.example}</div>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Initialize form values
  useEffect(() => {
    form.setFieldsValue({
      cmtStructureType: 'single',
      cmtTemplate: null,
      advancedCmtConfig: false
    });
  }, [form]);

  /**
   * 🏗️ Render configuration forms for selected template
   */
  const renderTemplateConfiguration = () => {
    if (!selectedTemplate) return null;

    const template = MULTI_CMT_TEMPLATES.find(t => t.id === selectedTemplate);
    
    return (
      <div>
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToSelection}
              style={{ marginRight: '16px' }}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Configure {template.name}
            </Title>
          </div>
        </div>

        <Card style={{ marginBottom: '24px' }}>
          {selectedTemplate === 'enterprise-global' && renderEnterpriseGlobalConfig()}
          {selectedTemplate === 'functional-matrix' && renderFunctionalMatrixConfig()}
          {selectedTemplate === 'regional-hub' && renderRegionalHubConfig()}
          {selectedTemplate === 'simple-hierarchy' && renderSimpleHierarchyConfig()}
        </Card>
      </div>
    );
  };

  /**
   * 🌍 Enterprise Global Configuration Form
   */
  const renderEnterpriseGlobalConfig = () => {
    
    return (
      <div>
        <Title level={5}>Global CMT Configuration</Title>
        
        <Form.Item 
          label="Global CMT Name"
          name={['cmtConfig', 'globalName']}
          rules={[{ required: true, message: 'Please enter the Global CMT name' }]}
        >
          <Input placeholder="e.g., Global Crisis Management Team" />
        </Form.Item>

        <Form.Item 
          label="Number of Regional CMTs"
          name={['cmtConfig', 'regionalCount']}
        >
          <InputNumber 
            min={1} 
            max={10} 
            value={regionalCount}
            onChange={setRegionalCount}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Divider />
        <Title level={5}>Regional CMT Details</Title>
        
        {Array.from({ length: regionalCount }, (_, index) => (
          <div key={index} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
            <Text strong>Regional CMT #{index + 1}</Text>
            <Row gutter={16} style={{ marginTop: '8px' }}>
              <Col span={12}>
                <Form.Item 
                  name={['cmtConfig', 'regional', index, 'name']}
                  rules={[{ required: true, message: 'Please enter regional CMT name' }]}
                >
                  <Input placeholder={`e.g., ${['North America', 'EMEA', 'APAC', 'Latin America', 'Africa', 'Australia'][index] || 'Regional CMT'}`} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name={['cmtConfig', 'regional', index, 'scope']}
                  rules={[{ required: true, message: 'Please enter geographic scope' }]}
                >
                  <Input placeholder="Geographic coverage area" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ))}
      </div>
    );
  };

  /**
   * 🔧 Functional Matrix Configuration Form
   */
  const renderFunctionalMatrixConfig = () => {
    const defaultFunctions = ['Operations', 'IT Security', 'Communications', 'Legal & Compliance', 'HR & Safety'];
    
    return (
      <div>
        <Title level={5}>Executive CMT</Title>
        
        <Form.Item 
          label="Executive CMT Name"
          name={['cmtConfig', 'executiveName']}
          rules={[{ required: true, message: 'Please enter the Executive CMT name' }]}
        >
          <Input placeholder="e.g., Executive Crisis Command" />
        </Form.Item>

        <Divider />
        <Title level={5}>Functional CMTs</Title>
        
        <Form.List name={['cmtConfig', 'functionalTeams']}>
          {(fields, { add, remove }) => (
            <>
              {fields.length === 0 && defaultFunctions.map((func, index) => {
                add({ name: func, responsibility: `Responsible for ${func.toLowerCase()} crisis response` });
              })}
              
              {fields.map(({ key, name, ...restField }, index) => (
                <div key={key} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        rules={[{ required: true, message: 'CMT name required' }]}
                      >
                        <Input placeholder="CMT Name" />
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item
                        {...restField}
                        name={[name, 'responsibility']}
                        rules={[{ required: true, message: 'Responsibility required' }]}
                      >
                        <Input placeholder="Primary responsibility/focus area" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
              
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Functional CMT
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>
    );
  };

  /**
   * 🗺️ Regional Hub Configuration Form
   */
  const renderRegionalHubConfig = () => {
    
    return (
      <div>
        <Form.Item 
          label="Number of Regional Hubs"
          name={['cmtConfig', 'regionCount']}
        >
          <InputNumber 
            min={2} 
            max={15} 
            value={regionCount}
            onChange={setRegionCount}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Divider />
        <Title level={5}>Regional Hub Details</Title>
        
        {Array.from({ length: regionCount }, (_, index) => (
          <div key={index} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
            <Text strong>Regional Hub #{index + 1}</Text>
            <Row gutter={16} style={{ marginTop: '8px' }}>
              <Col span={12}>
                <Form.Item 
                  name={['cmtConfig', 'regions', index, 'name']}
                  rules={[{ required: true, message: 'Please enter hub name' }]}
                >
                  <Input placeholder={`e.g., ${['Eastern Hub', 'Western Hub', 'Central Hub', 'Northern Hub', 'Southern Hub'][index] || 'Regional Hub'}`} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name={['cmtConfig', 'regions', index, 'boundaries']}
                  rules={[{ required: true, message: 'Please define boundaries' }]}
                >
                  <Input placeholder="Geographic boundaries/coverage" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ))}
      </div>
    );
  };

  /**
   * 🏢 Simple Hierarchy Configuration Form
   */
  const renderSimpleHierarchyConfig = () => {
    return (
      <div>
        <Title level={5}>Parent CMT</Title>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Parent CMT Name"
              name={['cmtConfig', 'parentName']}
              rules={[{ required: true, message: 'Please enter parent CMT name' }]}
            >
              <Input placeholder="e.g., Executive Crisis Team" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              label="Primary Focus"
              name={['cmtConfig', 'parentFocus']}
              rules={[{ required: true, message: 'Please enter primary focus' }]}
            >
              <Input placeholder="e.g., Strategic oversight and coordination" />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Title level={5}>Child CMTs</Title>
        
        <Form.List name={['cmtConfig', 'childTeams']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <div key={key} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        rules={[{ required: true, message: 'CMT name required' }]}
                      >
                        <Input placeholder="Child CMT Name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, 'focus']}
                        rules={[{ required: true, message: 'Focus area required' }]}
                      >
                        <Input placeholder="Focus area/responsibility" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                </div>
              ))}
              
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Child CMT
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>
    );
  };

  /**
   * 🎨 Render Visual Hierarchy Preview
   */
  const renderHierarchyPreview = () => {
    if (!hierarchyData) return null;

    const confirmHierarchy = () => {
      // Store final configuration in form
      form.setFieldsValue({
        hierarchyData: hierarchyData,  // THIS IS THE KEY FIELD FOR CMT ASSIGNMENT!
        cmtConfiguration: hierarchyData
      });
      message.success('CMT structure configured successfully!');
      // Could transition to next step here or return to selection
    };

    const editHierarchy = () => {
      setConfigurationStep('qa');
      setQaStep(0);
    };

    return (
      <div style={{ width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={3}>
            <ApartmentOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            Your CMT Hierarchy Preview
          </Title>
          <Paragraph type="secondary">
            Review your crisis management team structure below. You can edit or confirm this structure.
          </Paragraph>
        </div>

        <Card style={{ marginBottom: '24px', padding: '0', overflow: 'hidden' }}>
          <CMTStructureVisualization 
            hierarchyData={hierarchyData}
            readOnly={false}
          />
        </Card>

        <div style={{ textAlign: 'center' }}>
          <Space size="large">
            <Button size="large" onClick={editHierarchy}>
              <ArrowLeftOutlined /> Edit Structure
            </Button>
            <Button type="primary" size="large" onClick={confirmHierarchy}>
              Confirm Structure
            </Button>
          </Space>
        </div>
      </div>
    );
  };





  // Conditional rendering based on configuration step
  if (configurationStep === 'qa' && selectedTemplate) {
    return renderQAInterface();
  }

  if (configurationStep === 'preview' && hierarchyData) {
    return renderHierarchyPreview();
  }

  if (configurationStep === 'configuration' && selectedTemplate) {
    return renderTemplateConfiguration();
  }

  return (
    <div className="multi-cmt-configuration">
      <Title level={3} style={{ marginBottom: '8px', textAlign: 'center' }}>
        <ApartmentOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
        Crisis Management Team Structure
      </Title>
      
      <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: '16px', marginBottom: '32px' }}>
        Configure your organization's crisis management structure. Most organizations use a single team,
        but large enterprises may benefit from multi-team hierarchies.
      </Paragraph>

      <Form.Item name="cmtStructureType" style={{ marginBottom: '24px' }}>
        <div>
          {STRUCTURE_OPTIONS.map(renderStructureOption)}
        </div>
      </Form.Item>

      {/* Hidden form fields for data storage */}
      <Form.Item name="cmtTemplate" style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>
      
      <Form.Item name="advancedCmtConfig" style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>

      {/* Multi-CMT Template Selection */}
      {renderTemplateCards()}

      {/* Information Panel */}
      <div style={{ marginTop: '32px' }}>
        <Collapse ghost>
          <Panel 
            header={
              <span style={{ color: '#1890ff', fontWeight: 500 }}>
                <InfoCircleOutlined style={{ marginRight: '8px' }} />
                Learn More About CMT Structures
              </span>
            } 
            key="info"
          >
            <div style={{ backgroundColor: '#fafafa', padding: '16px', borderRadius: '6px' }}>
              <Title level={5}>Single Crisis Management Team</Title>
              <Paragraph style={{ fontSize: '13px', marginBottom: '16px' }}>
                Perfect for most organizations. Creates one centralized crisis management team with clear roles and responsibilities. 
                Team members can include leaders, safety officers, communications staff, and other key personnel.
              </Paragraph>
              
              <Title level={5}>Multi-CMT Enterprise Structure</Title>
              <Paragraph style={{ fontSize: '13px', marginBottom: '8px' }}>
                Designed for large organizations with complex hierarchies. Supports multiple crisis management teams with different 
                specializations and geographic coverage. Features include:
              </Paragraph>
              <ul style={{ fontSize: '13px', paddingLeft: '20px' }}>
                <li>Global oversight with regional autonomy</li>
                <li>Functional specialization (IT, Communications, Legal)</li>
                <li>Geographic distribution for site-level response</li>
                <li>Hierarchical coordination and escalation</li>
                <li>Cross-team communication and resource sharing</li>
              </ul>
            </div>
          </Panel>
        </Collapse>
      </div>

      {/* Guidance Alert */}
      <Alert
        message="Need Help Choosing?"
        description={
          <div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Choose Single CMT if:</strong> Your organization has under 500 employees, operates primarily in one region, or prefers centralized crisis management.
            </div>
            <div>
              <strong>Choose Multi-CMT if:</strong> You're a large enterprise with multiple business units, geographic regions, or need specialized crisis response teams.
            </div>
          </div>
        }
        type="info"
        showIcon
        style={{ marginTop: '24px' }}
      />
    </div>
  );
};

export default MultiCMTConfiguration;